import { useState, useEffect, useRef, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ArrowLeft, ExternalLink, Maximize2, Minimize2, FileText, Download, CheckCircle, Clock } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { toast } from "sonner";

interface QuotationFile {
  id: string;
  fileName: string;
  fileType: string;
  category: string;
  quotationNumber: string;
  savedAt: string;
  size: number;
}

export default function Quotations() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [recentFiles, setRecentFiles] = useState<QuotationFile[]>([]);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const fullscreenIframeRef = useRef<HTMLIFrameElement>(null);

  // Enviar idioma al iframe cuando cambie
  const sendLanguageToIframe = useCallback((iframe: HTMLIFrameElement | null) => {
    if (iframe?.contentWindow) {
      iframe.contentWindow.postMessage({
        type: 'ATHENEA_LANGUAGE_CHANGE',
        language: language
      }, '*');
    }
  }, [language]);

  // Escuchar mensajes de Perseo
  useEffect(() => {
    const handlePerseoMessage = async (event: MessageEvent) => {
      // Solo procesar mensajes de tipo PERSEO_FILE_GENERATED
      if (event.data?.type !== 'PERSEO_FILE_GENERATED') return;

      const { messageId, file, quotation } = event.data;
      console.log('Athenea: Archivo recibido de Perseo:', file.name);

      try {
        // Intentar subir al servidor
        const result = await uploadQuotationFile(file, quotation);

        // Agregar a la lista de archivos recientes
        const newFile: QuotationFile = {
          id: result.id || Date.now().toString(),
          fileName: file.name,
          fileType: file.type,
          category: file.category,
          quotationNumber: quotation.number,
          savedAt: new Date().toISOString(),
          size: file.size
        };
        setRecentFiles(prev => [newFile, ...prev.slice(0, 9)]);

        // Enviar confirmación a Perseo
        const targetIframe = isFullscreen ? fullscreenIframeRef.current : iframeRef.current;
        targetIframe?.contentWindow?.postMessage({
          messageId: messageId,
          success: true,
          fileId: result.id,
          fileUrl: result.url
        }, '*');

        toast.success(`Archivo guardado: ${file.name}`);

      } catch (error: any) {
        console.error('Athenea: Error guardando archivo:', error);

        // Enviar error a Perseo
        const targetIframe = isFullscreen ? fullscreenIframeRef.current : iframeRef.current;
        targetIframe?.contentWindow?.postMessage({
          messageId: messageId,
          success: false,
          error: error.message
        }, '*');

        toast.error(`Error guardando archivo: ${error.message}`);
      }
    };

    window.addEventListener('message', handlePerseoMessage);
    return () => window.removeEventListener('message', handlePerseoMessage);
  }, [isFullscreen]);

  // Enviar idioma cuando el iframe carga
  useEffect(() => {
    const handleIframeLoad = () => {
      setTimeout(() => {
        sendLanguageToIframe(iframeRef.current);
        sendLanguageToIframe(fullscreenIframeRef.current);
      }, 500);
    };

    const iframe = iframeRef.current;
    const fullscreenIframe = fullscreenIframeRef.current;

    iframe?.addEventListener('load', handleIframeLoad);
    fullscreenIframe?.addEventListener('load', handleIframeLoad);

    return () => {
      iframe?.removeEventListener('load', handleIframeLoad);
      fullscreenIframe?.removeEventListener('load', handleIframeLoad);
    };
  }, [sendLanguageToIframe]);

  // Enviar idioma cuando cambie
  useEffect(() => {
    sendLanguageToIframe(iframeRef.current);
    sendLanguageToIframe(fullscreenIframeRef.current);
  }, [language, sendLanguageToIframe]);

  // Función para subir archivo al servidor
  const uploadQuotationFile = async (file: any, quotation: any): Promise<{ id: string; url: string }> => {
    try {
      const response = await fetch('/api/quotation-files', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          file: {
            data: file.data,
            name: file.name,
            type: file.type,
            category: file.category,
            size: file.size
          },
          quotation: {
            number: quotation.number,
            clientName: quotation.clientName,
            projectName: quotation.projectName
          }
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Error uploading file');
      }

      const result = await response.json();
      console.log('Archivo guardado en servidor:', result);
      return { id: result.id, url: result.url };

    } catch (error: any) {
      console.error('Error subiendo archivo al servidor:', error);

      // Fallback a localStorage si el servidor falla
      const fileRecord = {
        id: `local_${Date.now()}`,
        url: `local://${file.name}`
      };
      const stored = JSON.parse(localStorage.getItem('perseo_files') || '[]');
      stored.unshift({ ...fileRecord, file, quotation, savedAt: new Date().toISOString() });
      localStorage.setItem('perseo_files', JSON.stringify(stored.slice(0, 50)));

      console.log('Fallback: Archivo guardado en localStorage');
      return fileRecord;
    }
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  const openInNewTab = () => {
    window.open("/perseo/sistema-cotizacion-magma.html", "_blank");
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  if (isFullscreen) {
    return (
      <div className="fixed inset-0 z-50 bg-background">
        <div className="absolute top-2 right-2 z-10 flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={openInNewTab}
            className="bg-background/80 backdrop-blur"
          >
            <ExternalLink className="w-4 h-4 mr-2" />
            {language === "es" ? "Nueva ventana" : "New window"}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={toggleFullscreen}
            className="bg-background/80 backdrop-blur"
          >
            <Minimize2 className="w-4 h-4 mr-2" />
            {language === "es" ? "Salir" : "Exit"}
          </Button>
        </div>
        <iframe
          ref={fullscreenIframeRef}
          src="/perseo/sistema-cotizacion-magma.html"
          className="w-full h-full border-0"
          title="Sistema de Cotización Perseo"
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5 p-4 sm:p-6">
      <div className="max-w-7xl mx-auto space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(-1)}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              {language === "es" ? "Volver" : "Back"}
            </Button>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold">
                {language === "es" ? "Sistema de Cotización" : "Quotation System"}
              </h1>
              <p className="text-muted-foreground text-sm">
                {language === "es"
                  ? "Perseo - Cotizaciones profesionales para Magma Composites"
                  : "Perseo - Professional quotations for Magma Composites"}
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={openInNewTab}
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              {language === "es" ? "Nueva ventana" : "New window"}
            </Button>
            <Button
              variant="default"
              size="sm"
              onClick={toggleFullscreen}
            >
              <Maximize2 className="w-4 h-4 mr-2" />
              {language === "es" ? "Pantalla completa" : "Fullscreen"}
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
          {/* Iframe Container */}
          <Card className="lg:col-span-3 overflow-hidden border-2">
            <iframe
              ref={iframeRef}
              src="/perseo/sistema-cotizacion-magma.html"
              className="w-full border-0"
              style={{ height: "calc(100vh - 180px)", minHeight: "600px" }}
              title="Sistema de Cotización Perseo"
            />
          </Card>

          {/* Recent Files Panel */}
          <Card className="p-4 space-y-4">
            <div className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-primary" />
              <h3 className="font-semibold">
                {language === "es" ? "Archivos Recientes" : "Recent Files"}
              </h3>
            </div>

            {recentFiles.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Clock className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">
                  {language === "es"
                    ? "Los archivos generados aparecerán aquí"
                    : "Generated files will appear here"}
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {recentFiles.map((file) => (
                  <div
                    key={file.id}
                    className="p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate" title={file.fileName}>
                          {file.fileName}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {file.quotationNumber} • {formatFileSize(file.size)}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {new Date(file.savedAt).toLocaleTimeString()}
                        </p>
                      </div>
                      <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}
